import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { AutoComplete, Button, Drawer, Form, Input, InputNumber, Radio, Space, Switch, Tag, Typography, message } from 'antd';
import { MessageOutlined, SaveOutlined } from '@ant-design/icons';
import { colors } from '@/theme/tokens';
import {
  PromptSendMode,
  savePromptLibraryRow,
  type AgentLibraryPrompt,
} from '@/api/agentPromptLibrary';

const { TextArea } = Input;
const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// PromptLibraryEditorDrawer — add or edit one ai.AgentPromptLibrary row.
//
// Same drawer for "New prompt" and the edit pencil. The Source line is read-only
// provenance: editing here never writes back to the test prompt it came from.
// Validation messages come from the server (PromptLibraryRules) so Studio and
// the chat can never disagree about what a valid prompt is; the form only adds
// the obvious required-field hints.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  agentId: number;
  /** Null = new prompt. */
  row: AgentLibraryPrompt | null;
  /** Categories already used by this agent, offered as suggestions. */
  categories: string[];
  /** "Test 4.3" for an imported row, resolved by the tab. */
  sourceLabel?: string | null;
  /** Order to give a new prompt so it lands at the end. */
  nextDisplayOrder: number;
  onClose: () => void;
  onSaved: (row: AgentLibraryPrompt) => void;
}

interface FormValues {
  title: string;
  category?: string;
  displayOrder: number;
  description?: string;
  prompt: string;
  sendMode: PromptSendMode;
  isActive: boolean;
}

export const PromptLibraryEditorDrawer = observer(function PromptLibraryEditorDrawer({
  open,
  agentId,
  row,
  categories,
  sourceLabel,
  nextDisplayOrder,
  onClose,
  onSaved,
}: Props) {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      title: row?.title ?? '',
      category: row?.category ?? undefined,
      displayOrder: row?.displayOrder ?? nextDisplayOrder,
      description: row?.description ?? undefined,
      prompt: row?.prompt ?? '',
      sendMode: row?.sendMode ?? PromptSendMode.Send,
      isActive: row?.isActive ?? true,
    });
  }, [open, row, nextDisplayOrder, form]);

  const save = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const saved = await savePromptLibraryRow({
        id: row?.id ?? 0,
        agentId,
        title: values.title,
        category: values.category ?? null,
        displayOrder: values.displayOrder ?? 0,
        description: values.description ?? null,
        prompt: values.prompt,
        sendMode: values.sendMode,
        isActive: values.isActive,
      });
      message.success(row ? 'Prompt saved' : 'Prompt added');
      onSaved(saved);
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Could not save the prompt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <MessageOutlined style={{ color: colors.violet[500] }} />
          <span>{row ? 'Edit prompt' : 'New prompt'}</span>
        </Space>
      }
      size={560}
      open={open}
      onClose={onClose}
      destroyOnHidden
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
            Save
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, whitespace: true, message: 'Give the prompt a title.' }, { max: 200 }]}
        >
          <Input placeholder="e.g. Visits in the last 12 months" maxLength={200} />
        </Form.Item>

        <Space.Compact block style={{ gap: 12 }}>
          <Form.Item name="category" label="Category" style={{ flex: 1 }} rules={[{ max: 120 }]}>
            <AutoComplete
              options={categories.map((c) => ({ value: c }))}
              placeholder="Groups the prompt in the chat drawer"
              filterOption={(input, option) => (option?.value ?? '').toLowerCase().includes(input.toLowerCase())}
              allowClear
            />
          </Form.Item>
          <Form.Item name="displayOrder" label="Order" style={{ width: 120 }}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Space.Compact>

        <Form.Item
          name="description"
          label="Description"
          extra="Optional. Shown under the title in the chat."
          rules={[{ max: 500 }]}
        >
          <Input maxLength={500} />
        </Form.Item>

        <Form.Item
          name="prompt"
          label="Prompt"
          rules={[{ required: true, whitespace: true, message: 'The prompt text is empty.' }]}
        >
          <TextArea autoSize={{ minRows: 4, maxRows: 12 }} placeholder="Exactly what the care manager sends" />
        </Form.Item>

        <Form.Item name="sendMode" label="On click">
          <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Radio value={PromptSendMode.Send}>
              Send it straight away{' '}
              <Text type="secondary" style={{ fontSize: 12 }}>
                Appears as the care manager's own message.
              </Text>
            </Radio>
            <Radio value={PromptSendMode.Draft}>
              Put it in the box to edit first{' '}
              <Text type="secondary" style={{ fontSize: 12 }}>
                Right for prompts that create something, or end in a blank the person fills in.
              </Text>
            </Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="isActive" label="Active" valuePropName="checked" extra="Off keeps the prompt here but hides it from the chat.">
          <Switch />
        </Form.Item>

        {row && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Source:{' '}
            {sourceLabel ? (
              <>
                <Tag style={{ fontFamily: "'JetBrains Mono', monospace" }}>{sourceLabel}</Tag>
                imported from Test prompts. Edits here never change the test prompt.
              </>
            ) : (
              'written in Studio.'
            )}
          </Text>
        )}
      </Form>
    </Drawer>
  );
});
